import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { payoutAccountKeys } from './keys'
import {
  getPayoutAccounts,
  createPayoutAccount,
  deletePayoutAccount,
  refreshPayoutAccountSession,
  getEventPayoutAccount,
  setEventPayoutAccount,
  syncPayoutAccount,
} from './service'
import type { ICreatePayoutAccountPayload, ISetEventPayoutAccountPayload } from '../types'

// Query: List payout accounts
export const usePayoutAccountsQuery = () => {
  return useQuery({
    queryKey: payoutAccountKeys.list(),
    queryFn: getPayoutAccounts,
  })
}

// Query: Event payout account
export const useEventPayoutAccountQuery = (eventId: number) => {
  return useQuery({
    queryKey: payoutAccountKeys.eventAccount(eventId),
    queryFn: () => getEventPayoutAccount(eventId),
    enabled: eventId > 0,
  })
}

// Mutation: Create account
export const useCreatePayoutAccountMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ICreatePayoutAccountPayload) => createPayoutAccount(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payoutAccountKeys.list() })
    },
  })
}

// Mutation: Delete account
export const useDeletePayoutAccountMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (accountId: number) => deletePayoutAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payoutAccountKeys.list() })
    },
  })
}

// Mutation: Refresh session
export const useRefreshPayoutSessionMutation = () => {
  return useMutation({
    mutationFn: (accountId: number) => refreshPayoutAccountSession(accountId),
  })
}

// Mutation: Set event payout account
export const useSetEventPayoutAccountMutation = (eventId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ISetEventPayoutAccountPayload) => setEventPayoutAccount({ eventId, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: payoutAccountKeys.eventAccount(eventId),
      })
    },
  })
}

// Mutation: Sync payout account status from Stripe
export const useSyncPayoutAccountMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (accountId: number) => syncPayoutAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payoutAccountKeys.list() })
    },
  })
}
